/* eslint-disable react/no-array-index-key */
/* eslint-disable react/jsx-props-no-spreading */
import React, { useCallback, useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import ContextMenu from "./ContextMenu";
import ToolTip from "./Tooltip";
import { CircleHelp } from "lucide-react";
import useIsMobile from "../../hooks/useIsMobile";

function EventLineItem({ row, updateTrigger }) {
  const [eventId, setEventId] = useState(null);
  const [eventDescription, setEventDescription] = useState(null);
  const [activityEventName, setActivityEventName] = useState(null);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const isMobile = useIsMobile();

  const mounted = useRef(false);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const dropdownToggle = () => {
    setDropdownOpen((prevState) => !prevState);
    setPopoverOpen(false);
  };

  const toggle = useCallback(() => {
    if (mounted.current && isMobile) {
      if (!dropdownOpen) {
        setPopoverOpen((prev) => !prev);
      }
    }
  }, [dropdownOpen, isMobile]);

  useEffect(() => {
    const eventIdCell = row.allCells.find((cell) => cell.column.id === "id");

    const eventDescriptionCell = row.allCells.find(
      (cell) => cell.column.id === "longDescription",
    );

    const activityEventNameCell = row.allCells.find(
      (cell) => cell.column.id === "activityEventName",
    );

    setEventId(eventIdCell.value);
    setEventDescription(eventDescriptionCell.value);
    setActivityEventName(activityEventNameCell.value);
  }, [row.allCells]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter') {
      setPopoverOpen((prev) => !prev);
    }
  }, []);

  if (!eventId) return null;

  return (
    <div
      role="button"
      {...row.getRowProps()}
      className="pp_comms_table__lineitem"
      id={isMobile ? `tool-tip-${eventId}` : undefined}
      onClick={isMobile ? toggle : undefined}
      onKeyDown={isMobile ? handleKeyDown : undefined}
      tabIndex={isMobile ? 0 : undefined}
    >
      {row.cells.map((cell, index) => {
        return (
          <span
            key={index}
            role={cell.column.Header === "Name" ? "button" : "cell"}
            {...cell.getCellProps()}
            className={`pp_comms_table__lineitem_name pp_comms_table__lineitem_name--${
              typeof cell.column.Header === "string"
                ? cell.column.Header.toLowerCase()
                : ""
            }`}
          >
            {cell.column.Header === "Status" && (
              <>
                <span
                  className={`pp_comms_table__status pp_comms_table__status--${cell.value === "INACTIVE" && "inactive" || "active"}`}
                >
                  {cell.render("Cell")}
                </span>
                <ContextMenu
                  updateTrigger={updateTrigger}
                  eventId={eventId}
                  content={cell.value}
                  activityEventName={activityEventName}
                  dropdownOpen={dropdownOpen}
                  toggle={dropdownToggle}
                />
              </>
            )}
            {
              cell.column.Header === "Name" && (
                <div className="tw-flex tw-items-center">
                  {cell.render("Cell")}
                  {!isMobile && (
                    <div id={`tool-tip-${eventId}`}>
                      <CircleHelp
                        role="button"
                        tabIndex={0}
                        onMouseEnter={() => {
                          setPopoverOpen(true);
                        }}
                        onMouseLeave={() => {
                          setPopoverOpen(false);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setPopoverOpen(true);
                          }
                        }}
                        onBlur={() => {
                          setPopoverOpen(false);
                        }}
                        className="tw-h-3.5 tw-w-3.5 tw-ml-2.5 tw-text-gray-500"
                      />
                    </div>
                  )}
                </div>
              )
            }
            {cell.column.Header !== "Status" && cell.column.Header !== "Name" && cell.render("Cell")}
          </span>
        );
      })}
      <ToolTip
        content={eventDescription}
        target={`tool-tip-${eventId}`}
        popoverOpen={popoverOpen}
        toggle={isMobile ? toggle : undefined}
      />
    </div>
  );
}

EventLineItem.propTypes = {
  updateTrigger: PropTypes.func.isRequired,
  row: PropTypes.shape({
    getRowProps: PropTypes.func,
    cells: PropTypes.arrayOf(
      PropTypes.shape({
        getCellProps: PropTypes.func,
        render: PropTypes.func,
      }),
    ),
    allCells: PropTypes.arrayOf(
      PropTypes.shape({
        getCellProps: PropTypes.func,
        render: PropTypes.func,
      }),
    ),
  }).isRequired,
};

export default EventLineItem;
